import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import MetricCard from "@/components/common/MetricCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DASHBOARD } from "@/constants/testIds";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const DAY_PRESETS = [7, 14, 30, 90];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-strong rounded-lg px-3 py-2 text-xs border border-white/10">
      <div className="text-white/50 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-white">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="capitalize">{p.name}:</span>
          <span className="font-medium ml-auto">{p.value?.toLocaleString?.() ?? p.value}</span>
        </div>
      ))}
    </div>
  );
};

function calcDelta(arr, key) {
  if (!arr || arr.length < 2) return { delta: 0, label: "vs prior week" };
  const recent = arr.slice(-7);
  const previous = arr.slice(-14, -7);
  const recentSum = recent.reduce((a, d) => a + (d[key] || 0), 0);
  const previousSum = previous.reduce((a, d) => a + (d[key] || 0), 0);
  if (previousSum === 0) return { delta: recentSum > 0 ? 100 : 0, label: "vs prior week" };
  return {
    delta: Math.round(((recentSum - previousSum) / previousSum) * 100),
    label: "vs prior week",
  };
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(30);

  const load = async (days = rangeDays) => {
    setLoading(true);
    try {
      const { data: next } = await api.get("/dashboard/overview", { params: { days } });
      setData(next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(rangeDays); }, [rangeDays]); // eslint-disable-line

  const m = data?.metrics || {};
  const daily = (data?.daily || []).map((d) => ({
    ...d,
    label: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  const visitorsDelta = calcDelta(daily, "visitors");
  const viewsDelta = calcDelta(daily, "page_views");
  const leadsDelta = calcDelta(daily, "leads");
  const rangeVisitors = daily.reduce((sum, d) => sum + (d.visitors || 0), 0);
  const rangeViews = daily.reduce((sum, d) => sum + (d.page_views || 0), 0);
  const rangeLeads = daily.reduce((sum, d) => sum + (d.leads || 0), 0);
  const hasTraffic = rangeVisitors > 0 || rangeViews > 0;

  return (
    <div data-testid={DASHBOARD.root}>
      <PageHeader
        title="Dashboard"
        subtitle="Live overview of CMS content and real website traffic."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 glass rounded-lg p-0.5">
              {DAY_PRESETS.map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setRangeDays(days)}
                  className={`h-8 px-3 rounded-md text-xs ${rangeDays === days ? "bg-white/10 text-white" : "text-white/55 hover:text-white"}`}
                >
                  {days}d
                </button>
              ))}
            </div>
            <Button asChild variant="ghost" className="h-8 text-white/80 border border-white/10">
              <Link to="/analytics">
                <Icons.LineChart className="w-3.5 h-3.5 mr-1.5" /> Analytics
              </Link>
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {loading ? Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl bg-white/[0.03]" />
        )) : (
          <>
            <MetricCard testId={DASHBOARD.metricServices} label="Visitors" value={rangeVisitors.toLocaleString()} delta={visitorsDelta.delta} deltaLabel={visitorsDelta.label} icon="Users" accent="#8B5CF6" />
            <MetricCard testId={DASHBOARD.metricPortfolio} label="Page views" value={rangeViews.toLocaleString()} delta={viewsDelta.delta} deltaLabel={viewsDelta.label} icon="Eye" accent="#3B82F6" />
            <MetricCard testId={DASHBOARD.metricLeads} label="New leads" value={m.new_leads ?? 0} delta={leadsDelta.delta} deltaLabel={`${rangeLeads} in ${rangeDays}d`} icon="Inbox" accent="#06B6D4" />
            <MetricCard testId={DASHBOARD.metricRevenue} label="Content" value={(m.services ?? 0) + (m.portfolio ?? 0) + (m.blog ?? 0)} delta={0} deltaLabel={`${m.services ?? 0} svc · ${m.portfolio ?? 0} work · ${m.blog ?? 0} posts`} icon="Layers" accent="#10B981" />
          </>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {loading ? Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl bg-white/[0.03]" />
        )) : (
          <>
            <GlassCard className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{m.services ?? 0}</div>
              <div className="text-xs text-white/50 mt-1">Services</div>
            </GlassCard>
            <GlassCard className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{m.portfolio ?? 0}</div>
              <div className="text-xs text-white/50 mt-1">Portfolio</div>
            </GlassCard>
            <GlassCard className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{m.blog ?? 0}</div>
              <div className="text-xs text-white/50 mt-1">Blog posts</div>
            </GlassCard>
            <GlassCard className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{m.users ?? 0}</div>
              <div className="text-xs text-white/50 mt-1">Admin users</div>
            </GlassCard>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <GlassCard className="xl:col-span-2 p-6" data-testid={DASHBOARD.chartVisitors}>
          <div className="flex items-start justify-between mb-6 gap-3">
            <div>
              <h3 className="text-sm font-medium text-white">Traffic overview</h3>
              <p className="text-xs text-white/40 mt-0.5">Real pageviews from the public site · last {rangeDays} days</p>
            </div>
            <div className="flex items-center gap-3 text-xs shrink-0">
              <div className="flex items-center gap-1.5 text-white/70"><span className="w-2 h-2 rounded-full bg-[#8B5CF6]" /> Visitors</div>
              <div className="flex items-center gap-1.5 text-white/70"><span className="w-2 h-2 rounded-full bg-[#06B6D4]" /> Page views</div>
            </div>
          </div>
          <div className="h-72">
            {hasTraffic ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily} margin={{ left: -10, right: 0, top: 5, bottom: 0 }}>
                  <defs>
                    <linearGradient id="v1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="v2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="visitors" stroke="#8B5CF6" strokeWidth={2} fill="url(#v1)" />
                  <Area type="monotone" dataKey="page_views" stroke="#06B6D4" strokeWidth={2} fill="url(#v2)" name="Page views" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center gap-2 px-6">
                <Icons.Activity className="w-8 h-8 text-white/25" />
                <p className="text-sm text-white/50">No live traffic in this range yet.</p>
                <p className="text-xs text-white/35 max-w-sm">
                  Visits are recorded when people browse the public website. Open Analytics to rebuild after clearing old demo data.
                </p>
                <Button asChild size="sm" className="mt-2 btn-gradient text-white border-0">
                  <Link to="/analytics">Open Analytics</Link>
                </Button>
              </div>
            )}
          </div>
        </GlassCard>

        <GlassCard className="p-6" data-testid={DASHBOARD.recentActivity}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-white">Recent activity</h3>
            <Icons.Activity className="w-4 h-4 text-white/40" />
          </div>
          <div className="space-y-3">
            {(data?.recent_activity || []).slice(0, 8).map((a, i) => (
              <div key={a.id || i} className="flex items-start gap-3 text-xs">
                <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  <Icons.User className="w-3.5 h-3.5 text-white/50" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white/90 truncate">
                    <span className="font-medium">{a.user_name || a.user_email || "System"}</span>{" "}
                    <span className="text-white/50">{a.action}</span>{" "}
                    <span className="text-white/70">{a.resource}</span>
                  </div>
                  <div className="text-[10px] text-white/40 mt-0.5">
                    {a.timestamp ? new Date(a.timestamp).toLocaleString() : "—"}
                  </div>
                </div>
              </div>
            ))}
            {!loading && !(data?.recent_activity || []).length && (
              <div className="text-xs text-white/40 py-4 text-center">No activity yet.</div>
            )}
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-0" data-testid={DASHBOARD.recentLeads}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <div>
            <h3 className="text-sm font-medium text-white">Recent leads</h3>
            <p className="text-xs text-white/40 mt-0.5">Latest inquiries from the website contact form</p>
          </div>
          <Link to="/leads" className="text-xs text-[#93C5FD] hover:text-white transition-colors duration-200 flex items-center gap-1">
            View all <Icons.ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-white/45 bg-white/[0.02]">
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Email</th>
                <th className="px-6 py-3 font-medium">Source</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_leads || []).map((l) => (
                <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-6 py-3 text-white/90">{l.name}</td>
                  <td className="px-6 py-3 text-white/60">{l.email}</td>
                  <td className="px-6 py-3 text-white/60 capitalize">{(l.source || "").replace("_", " ")}</td>
                  <td className="px-6 py-3"><StatusBadge value={l.status} /></td>
                  <td className="px-6 py-3 text-white/50 text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {!loading && !(data?.recent_leads || []).length && (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-white/40 text-sm">No leads yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}
